import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowRight, Save, Wand2, X } from 'lucide-react';
import vendorApi from '../../../api/vendorApi';
import { NormalizedDescriptionOverride, Transaction } from '../../../api/types';

interface NormalizedDescriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    transaction: Transaction | null | undefined;
    businessId?: string;
    initialSuggestion?: {
        matchType?: 'contains' | 'regex' | 'exact' | string;
        matchValue?: string;
        normalizedDescription?: string;
        vendorName?: string;
    } | null;
    onSaved?: (payload: { normalizedDescription: string; vendorName?: string }) => void;
}

const collapseSpaces = (value: string): string => value.replace(/\s+/g, ' ').trim();

const toNormalizedCandidate = (value?: string | null): string => {
    return collapseSpaces((value || '').toUpperCase());
};

const stripProcessorNoise = (value?: string | null): string => {
    const raw = collapseSpaces(value || '');
    if (!raw) return '';
    const starIndex = raw.indexOf('*');
    if (starIndex >= 0 && starIndex < raw.length - 1) {
        return collapseSpaces(raw.slice(starIndex + 1));
    }
    return raw;
};

const ruleMatchesTransaction = (
    rule: NormalizedDescriptionOverride,
    transaction?: Transaction | null,
): boolean => {
    const originalDescription = (transaction?.original_description || '').trim();
    const direction = (transaction?.direction || '').trim().toLowerCase();
    if (!originalDescription) return false;

    const ruleDirection = String(rule.direction || '').trim().toLowerCase();
    if (ruleDirection && direction && ruleDirection !== direction) {
        return false;
    }

    const matchType = String(rule.match_type || 'contains').trim().toLowerCase();
    const matchValue = String(rule.match_value || '').trim();
    if (!matchValue) return false;

    if (matchType === 'exact') {
        return originalDescription.toUpperCase() === matchValue.toUpperCase();
    }
    if (matchType === 'regex') {
        try {
            return new RegExp(matchValue, 'i').test(originalDescription);
        } catch {
            return false;
        }
    }
    return originalDescription.toUpperCase().includes(matchValue.toUpperCase());
};

const suggestMatchValue = (transaction?: Transaction | null): string => {
    const original = collapseSpaces(transaction?.original_description || '');
    const vendorName = collapseSpaces(transaction?.vendor_name || '');
    const normalized = collapseSpaces(transaction?.normalized_description || '');
    const originalUpper = original.toUpperCase();
    if (vendorName && originalUpper.includes(vendorName.toUpperCase())) {
        return vendorName;
    }
    if (normalized && normalized.length > 3 && originalUpper.includes(normalized.toUpperCase())) {
        return normalized;
    }
    const stripped = stripProcessorNoise(original);
    if (stripped && stripped !== original) {
        return stripped;
    }
    return original;
};

const suggestVendorName = (transaction?: Transaction | null): string => {
    const vendorName = collapseSpaces(transaction?.vendor_name || '');
    if (vendorName && !['unknown', 'check', 'deposit', 'transfer', 'pay'].includes(vendorName.toLowerCase())) {
        return vendorName;
    }
    const normalized = collapseSpaces(transaction?.normalized_description || '');
    if (normalized) {
        return normalized
            .toLowerCase()
            .split(' ')
            .map((part) => part ? `${part[0].toUpperCase()}${part.slice(1)}` : '')
            .join(' ');
    }
    return '';
};

const buildInitialState = (transaction?: Transaction | null) => {
    const originalDescription = transaction?.original_description || '';
    const currentNormalized = transaction?.normalized_description || '';
    const suggestedMatchValue = suggestMatchValue(transaction);
    const suggestedReplacement = toNormalizedCandidate(
        transaction?.vendor_name || stripProcessorNoise(originalDescription) || currentNormalized
    );
    return {
        matchType: 'contains',
        matchValue: suggestedMatchValue,
        replacementNormalizedDescription: suggestedReplacement || toNormalizedCandidate(currentNormalized),
        vendorName: suggestVendorName(transaction),
    };
};

const NormalizedDescriptionModal: React.FC<NormalizedDescriptionModalProps> = ({
    isOpen,
    onClose,
    transaction,
    businessId,
    initialSuggestion,
    onSaved,
}) => {
    const queryClient = useQueryClient();
    const initialState = useMemo(() => buildInitialState(transaction), [transaction]);
    const [matchType, setMatchType] = useState<'contains' | 'regex' | 'exact'>('contains');
    const [matchValue, setMatchValue] = useState('');
    const [replacementNormalizedDescription, setReplacementNormalizedDescription] = useState('');
    const [vendorName, setVendorName] = useState('');

    const { data: overrides = [] } = useQuery({
        queryKey: ['normalizedDescriptionOverrides', businessId, transaction?._id],
        queryFn: () => vendorApi.getNormalizedDescriptionOverrides(businessId, transaction?.original_description || transaction?.normalized_description || ''),
        enabled: isOpen && !!businessId && !!transaction?.original_description,
    });

    const existingOverride = useMemo(() => {
        if (!transaction) return null;
        return [...overrides]
            .filter((rule) => rule.active !== false)
            .sort((a, b) => {
                const priorityDelta = Number(b.priority || 0) - Number(a.priority || 0);
                if (priorityDelta !== 0) return priorityDelta;
                return String(b.updated_at || '').localeCompare(String(a.updated_at || ''));
            })
            .find((rule) => ruleMatchesTransaction(rule, transaction)) || null;
    }, [overrides, transaction]);

    useEffect(() => {
        if (!isOpen) return;
        if (existingOverride) {
            setMatchType((existingOverride.match_type as 'contains' | 'regex' | 'exact') || 'contains');
            setMatchValue(existingOverride.match_value || '');
            setReplacementNormalizedDescription(existingOverride.replacement_normalized_description || '');
            setVendorName(initialSuggestion?.vendorName || suggestVendorName(transaction));
            return;
        }
        setMatchType((initialSuggestion?.matchType as 'contains' | 'regex' | 'exact') || (initialState.matchType as 'contains' | 'regex' | 'exact'));
        setMatchValue(initialSuggestion?.matchValue || initialState.matchValue);
        setReplacementNormalizedDescription(initialSuggestion?.normalizedDescription || initialState.replacementNormalizedDescription);
        setVendorName(initialSuggestion?.vendorName || initialState.vendorName);
    }, [existingOverride, initialState, initialSuggestion, isOpen, transaction]);

    const createOverrideMutation = useMutation({
        mutationFn: () => vendorApi.createNormalizedDescriptionOverride({
            match_type: matchType,
            match_value: matchValue,
            replacement_normalized_description: replacementNormalizedDescription,
            direction: transaction?.direction || 'outflow',
            priority: 100,
            active: true,
        }, businessId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['normalizedDescriptionOverrides', businessId] });
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['transaction', transaction?._id, businessId] });
        },
    });

    const canSave = !!collapseSpaces(matchValue) && !!collapseSpaces(replacementNormalizedDescription);

    const runSave = async (openVendorMapping: boolean) => {
        const normalizedDescription = toNormalizedCandidate(replacementNormalizedDescription);
        await createOverrideMutation.mutateAsync();
        if (openVendorMapping) {
            onSaved?.({
                normalizedDescription,
                vendorName: collapseSpaces(vendorName) || undefined,
            });
        } else {
            onClose();
        }
    };

    if (!isOpen || !transaction) return null;

    return (
        <div className="fixed inset-0 z-[95] overflow-y-auto" aria-labelledby="normalized-description-modal" role="dialog" aria-modal="true">
            <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-slate-900/50 transition-opacity" aria-hidden="true" onClick={onClose} />
                <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>

                <div className="inline-block w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white text-left align-bottom shadow-2xl transition-all sm:my-8 sm:align-middle">
                    <div className="border-b border-slate-100 px-6 py-5">
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="mb-2 inline-flex rounded-full bg-indigo-50 p-2 text-indigo-600">
                                    <Wand2 size={18} />
                                </div>
                                <h3 id="normalized-description-modal" className="text-lg font-semibold text-slate-900">
                                    Update normalization rule
                                </h3>
                                <p className="mt-1 text-sm text-slate-500">
                                    Save a business-specific rule for all matching transactions in this business, then continue to vendor mapping if you want to refine the alias.
                                </p>
                            </div>
                            <button onClick={onClose} className="rounded-md p-2 text-slate-500 hover:bg-slate-100">
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    <div className="space-y-6 px-6 py-5">
                        <div className="grid gap-4 rounded-xl bg-slate-50 p-4 md:grid-cols-3">
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Original Description</p>
                                <p className="mt-1 break-all text-sm font-medium text-slate-800">{transaction.original_description || '---'}</p>
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Pipeline Output</p>
                                <p className="mt-1 text-sm font-medium text-slate-800">
                                    {transaction.original_normalized_description || transaction.normalized_description || '---'}
                                </p>
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Current Key</p>
                                <p className="mt-1 text-sm font-medium text-slate-800">{transaction.normalized_description || '---'}</p>
                            </div>
                        </div>

                        <div className="grid gap-5 md:grid-cols-2">
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                    When original description
                                </label>
                                <div className="flex gap-2">
                                    <select
                                        value={matchType}
                                        onChange={(e) => setMatchType(e.target.value as 'contains' | 'regex' | 'exact')}
                                        className="w-32 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                    >
                                        <option value="contains">contains</option>
                                        <option value="exact">equals</option>
                                        <option value="regex">regex</option>
                                    </select>
                                    <input
                                        type="text"
                                        value={matchValue}
                                        onChange={(e) => setMatchValue(e.target.value)}
                                        className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                        placeholder="ROYAL PALMS"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                    Set normalized description to
                                </label>
                                <input
                                    type="text"
                                    value={replacementNormalizedDescription}
                                    onChange={(e) => setReplacementNormalizedDescription(e.target.value)}
                                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                    placeholder="ROYAL PALMS"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Suggested Vendor Name For Mapping
                            </label>
                            <input
                                type="text"
                                value={vendorName}
                                onChange={(e) => setVendorName(e.target.value)}
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                placeholder="Royal Palms"
                            />
                            <p className="mt-1 text-xs text-slate-500">
                                This pre-fills the next vendor mapping step. You can change it there too.
                            </p>
                        </div>

                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            <div className="flex gap-2">
                                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                                <p>
                                    This creates a business rule for all matching transactions in this business. Existing matching transactions update immediately, and future transactions use it automatically.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => runSave(false)}
                            disabled={!canSave || createOverrideMutation.isPending}
                            className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {createOverrideMutation.isPending ? 'Saving...' : 'Save only'}
                        </button>
                        <button
                            type="button"
                            onClick={() => runSave(true)}
                            disabled={!canSave || createOverrideMutation.isPending}
                            className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <Save size={16} />
                            Save and open vendor mapping
                            <ArrowRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NormalizedDescriptionModal;
